import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/** pull-to-refresh で実行する再取得処理。await 可能。 */
export type RefreshFn = () => void | Promise<void>;

type RefreshContextValue = {
  register: (fn: RefreshFn | null) => void;
  getHandler: () => RefreshFn | null;
};

const RefreshContext = createContext<RefreshContextValue>({
  register: () => {/* default noop */},
  getHandler: () => null,
});

/**
 * 現在表示中の画面が pull-to-refresh でどう再取得するかを ref で保持する。
 * 値は ref に入れるだけなので、画面遷移しても Provider 配下を再レンダリングしない。
 */
export const RefreshProvider = ({ children }: { children: ReactNode }) => {
  const ref = useRef<RefreshFn | null>(null);
  const register = useCallback((fn: RefreshFn | null) => {
    ref.current = fn;
  }, []);
  const getHandler = useCallback(() => ref.current, []);
  const value = useMemo(() => ({ register, getHandler }), [register, getHandler]);
  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
};

/**
 * この画面が pull-to-refresh で実行する再取得処理を登録する。
 * `handler` は useCallback で安定させること（毎レンダリング登録し直しを避ける）。
 * アンマウント時に解除し、未登録の画面は MobileShell 側で全体リロードにフォールバックする。
 */
export const useRefreshHandler = (handler: RefreshFn) => {
  const { register } = useContext(RefreshContext);
  useEffect(() => {
    register(handler);
    return () => register(null);
  }, [handler, register]);
};

/** MobileShell から、現在登録されている再取得処理を取り出すための getter を得る。 */
export const useRefreshTarget = () => useContext(RefreshContext).getHandler;
